import { Module } from 'vuex'
import { FlowState, WizardMode } from './interface'
import { defaultBasic } from '@/utils/Flows'
import { Flow, Screen } from '@/interfaces/FlowInterfaces'
import _ from 'lodash'
import { ValidationError } from '@/services/errorHandler.service'
import {
  getFormChildren,
  isWizardCompatible,
  normalizeForWizard,
  serializeFromWizardForSave
} from '@/utils/flowWizardAdapter'
import {
  createFlowFromJson,
  updateFlowFromJson,
  updateFlowVisibleName,
  FlowWriteResult
} from '@/utils/flowWriteApi'
import { fetchFlowJsonById } from '@/utils/fetchFlowJson'

const initialState = (): FlowState => ({
  currentStep: '1',
  flowName: 'Flow',
  visibleName: '',
  flowJson: _.cloneDeep(defaultBasic),
  currentScreen: 0,
  jsonEditorKey: 0,
  stepTwoErrors: [],
  isSavingFlow: false,
  showJsonEditor: false,
  wizardMode: 'create',
  editingFlowId: null,
  whatsappApiSenderId: null
})

const state: FlowState = initialState()

const mutations = {
  SET_CURRENT_STEP(state: FlowState, newValue: string) {
    state.currentStep = newValue
  },
  SET_FLOW_JSON(state: FlowState, newValue: Flow) {
    state.flowJson = newValue
  },
  SET_FLOW_NAME(state: FlowState, newValue: string) {
    state.flowName = newValue
  },
  SET_VISIBLE_NAME(state: FlowState, newValue: string) {
    state.visibleName = newValue
  },
  SET_WIZARD_MODE(state: FlowState, newValue: WizardMode) {
    state.wizardMode = newValue
  },
  SET_EDITING_FLOW_ID(state: FlowState, newValue: number | null) {
    state.editingFlowId = newValue
  },
  SET_WHATSAPP_API_SENDER_ID(state: FlowState, newValue: number | null) {
    state.whatsappApiSenderId = newValue
  },
  ADD_SCREEN(state: FlowState, newValue: Screen) {
    state.flowJson.screens.push(newValue)
  },
  SET_CURRENT_TITLE(state: FlowState, newValue: string) {
    state.flowJson.screens[state.currentScreen].title = newValue
  },
  SET_CURRENT_SCREEN(state: FlowState, newValue: number) {
    state.currentScreen = newValue
  },
  ADD_CHILD(state: FlowState, newChild: any) {
    const childrenArray = getFormChildren(state.flowJson.screens[state.currentScreen])
    const footerIndex = findFooterIndex(childrenArray)

    if (footerIndex > -1) {
      childrenArray.splice(footerIndex, 0, newChild)
    } else {
      childrenArray.push(newChild)
    }
  },
  DELETE_CHILD(state: FlowState, childIndex: number) {
    const childrenArray = getFormChildren(state.flowJson.screens[state.currentScreen])

    if (childIndex >= 0 && childIndex < childrenArray.length) {
      childrenArray.splice(childIndex, 1)
    }
  },
  DELETE_SCREEN(state: FlowState, screenIndex: number) {
    if (screenIndex >= 0 && screenIndex < state.flowJson.screens.length) {
      state.flowJson.screens.splice(screenIndex, 1)

      if (state.currentScreen >= state.flowJson.screens.length) {
        state.currentScreen = Math.max(0, state.flowJson.screens.length - 1)
      }
    }
  },
  UPDATE_CHILD(state: FlowState, { childIndex, newChild }: { childIndex: number; newChild: any }) {
    const childrenArray = getFormChildren(state.flowJson.screens[state.currentScreen])

    if (childIndex >= 0 && childIndex < childrenArray.length) {
      childrenArray[childIndex] = newChild
    }
  },
  INCREMENT_JSON_EDITOR_KEY(state: FlowState) {
    state.jsonEditorKey += 1
  },
  SET_STEP_TWO_ERRORS(state: FlowState, newValue: any) {
    state.stepTwoErrors = newValue
  },
  SET_IS_SAVING_FLOW(state: FlowState, newValue: boolean) {
    state.isSavingFlow = newValue
  },
  SET_SHOW_JSON_EDITOR(state: FlowState, newValue: boolean) {
    state.showJsonEditor = newValue
  },
  RESET_WIZARD_STATE(state: FlowState, payload?: { template?: Flow }) {
    const fresh = initialState()

    Object.assign(state, fresh)

    if (payload?.template) {
      state.flowJson = _.cloneDeep(payload.template)
    }
  }
}

const getters = {
  currentScreen: (state: FlowState) => {
    return state.flowJson.screens[state.currentScreen]
  },

  currentFooter: (state: FlowState) => {
    const currentScreen = state.flowJson.screens[state.currentScreen]

    return getFormChildren(currentScreen).find((child: any) => child.type === 'Footer')
  },

  wizardMode: (state: FlowState) => state.wizardMode
}

const actions = {
  reloadJsonEditor({ commit }: { commit: any }) {
    commit('INCREMENT_JSON_EDITOR_KEY')
  },

  resetWizard(
    { commit }: { commit: any },
    payload: { mode: WizardMode; senderId: number; template?: Flow }
  ) {
    commit('RESET_WIZARD_STATE', { template: payload.template })
    commit('SET_WIZARD_MODE', payload.mode)
    commit('SET_WHATSAPP_API_SENDER_ID', payload.senderId)
    commit('SET_CURRENT_STEP', '1')
    commit('SET_CURRENT_SCREEN', 0)
    commit('SET_STEP_TWO_ERRORS', [])
    commit('SET_SHOW_JSON_EDITOR', false)
  },

  async loadFlowForEdit(
    { commit }: { commit: any },
    payload: {
      flowId: number
      senderId: number
      flowName?: string
      visibleName?: string
    }
  ) {
    const rawFlow = await fetchFlowJsonById(payload.flowId)
    const compatibility = isWizardCompatible(rawFlow as Record<string, unknown>)

    if (!compatibility.compatible) {
      throw new ValidationError(compatibility.reason || 'Flow incompatible con el asistente.')
    }

    const normalized = normalizeForWizard(rawFlow as Record<string, unknown>)

    commit('RESET_WIZARD_STATE')
    commit('SET_WIZARD_MODE', 'edit')
    commit('SET_EDITING_FLOW_ID', payload.flowId)
    commit('SET_WHATSAPP_API_SENDER_ID', payload.senderId)
    commit('SET_FLOW_NAME', payload.flowName || payload.visibleName || 'Flow')
    commit('SET_VISIBLE_NAME', payload.visibleName || payload.flowName || '')
    commit('SET_FLOW_JSON', normalized)
    commit('SET_CURRENT_STEP', '1')
    commit('SET_CURRENT_SCREEN', 0)
    commit('SET_STEP_TWO_ERRORS', [])
    commit('SET_SHOW_JSON_EDITOR', false)
  },

  async saveFlow(
    { commit, state }: { commit: any; state: FlowState },
    payload: { publish: boolean; originalVisibleName?: string }
  ): Promise<FlowWriteResult> {
    if (!state.whatsappApiSenderId) {
      throw new ValidationError('No se encontró el remitente de WhatsApp.')
    }

    commit('SET_IS_SAVING_FLOW', true)

    try {
      const { flow: flowJson, validationErrors } = serializeFromWizardForSave(state.flowJson, {
        mode: state.wizardMode
      })

      if (validationErrors.length) {
        throw new ValidationError('Flow JSON inválido para Meta', {
          validation_errors: validationErrors
        })
      }

      let result: FlowWriteResult

      if (state.wizardMode === 'create') {
        const trimmedName = state.flowName.trim()

        if (!trimmedName) {
          throw new ValidationError('El nombre del flow es requerido.')
        }

        result = await createFlowFromJson({
          whatsapp_api_sender_id: state.whatsappApiSenderId,
          name: trimmedName,
          flow_json: flowJson,
          publish: payload.publish
        })
      } else {
        if (!state.editingFlowId) {
          throw new ValidationError('No se encontró el flow a editar.')
        }

        const trimmedVisibleName = state.visibleName.trim()
        const originalVisibleName = (payload.originalVisibleName || '').trim()

        if (trimmedVisibleName && trimmedVisibleName !== originalVisibleName) {
          await updateFlowVisibleName(state.editingFlowId, trimmedVisibleName)
        }

        result = await updateFlowFromJson(state.editingFlowId, {
          flow_json: flowJson,
          publish: payload.publish
        })
      }

      return result
    } finally {
      commit('SET_IS_SAVING_FLOW', false)
    }
  }
}

const findFooterIndex = (children: any[]): number => {
  return children.findIndex((child: any) => child.type === 'Footer')
}

const flowModule: Module<FlowState, any> = {
  namespaced: true,
  getters,
  state,
  actions,
  mutations
}

export default flowModule
